import { useState, useEffect, useRef } from 'react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import {
  Phone, PhoneOff, Play, Pause, Trash2, ChevronDown, ChevronUp,
  Loader2, Search, Calendar, User, Clock, FileText, Sparkles, Mic,
  Brain, Target, MapPin, IndianRupee, Tag,
} from 'lucide-react';

interface TranscriptionMeta {
  suggestedDisposition?: string;
  suggestedNotes?: string;
  sentiment?: string;
  suggestedAction?: string;
  customerIntent?: {
    product?: string;
    location?: string;
    budget?: string;
    type?: string;
  };
}

interface CallRecord {
  id: string;
  contactId: string;
  agentId: string;
  phoneNumber: string;
  status: string;
  duration: number;
  startTime: string | null;
  endTime: string | null;
  notes: string | null;
  recordingUrl: string | null;
  recordingDuration: number | null;
  transcription: string | null;
  transcriptionSummary: string | null;
  transcriptionMetadata: TranscriptionMeta | null;
  sentiment: string | null;
  disposition: { id: string; name: string; category: string; color: string } | null;
  contact: { id: string; firstName: string; lastName: string; phone: string; mobile: string } | null;
  agent: { id: string; firstName: string; lastName: string } | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

function formatDurationStr(seconds: number | null): string {
  if (!seconds || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatDate(dateStr: string | null, tz?: string): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', ...(tz ? { timeZone: tz } : {}) };
  const timeOpts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true, ...(tz ? { timeZone: tz } : {}) };
  return d.toLocaleDateString('en-IN', opts) + ' ' + d.toLocaleTimeString('en-IN', timeOpts);
}

function statusBadge(status: string) {
  const colors: Record<string, string> = {
    CONNECTED: 'bg-green-50 text-green-700',
    NO_ANSWER: 'bg-red-50 text-red-700',
    BUSY: 'bg-amber-50 text-amber-700',
    FAILED: 'bg-gray-100 text-gray-600',
    VOICEMAIL: 'bg-blue-50 text-blue-700',
  };
  return colors[status] || 'bg-gray-100 text-gray-600';
}

// ─── Audio Player ───

function AudioPlayer({ callId }: { callId: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const audioUrl = `/api/v1/telecaller/calls/${callId}/recording`;

  useEffect(() => {
    const audio = new Audio();
    audio.src = audioUrl;
    audio.preload = 'metadata';
    audioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
      setLoading(false);
    });
    audio.addEventListener('timeupdate', () => setCurrentTime(audio.currentTime));
    audio.addEventListener('ended', () => setPlaying(false));
    audio.addEventListener('error', () => { setError(true); setLoading(false); });

    return () => {
      audio.pause();
      audio.src = '';
    };
  }, [callId]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  };

  const seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  if (error) return <p className="text-xs text-red-500">Recording unavailable</p>;
  if (loading) return <div className="flex items-center gap-2 text-xs text-gray-400"><Loader2 size={14} className="animate-spin" /> Loading audio...</div>;

  return (
    <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2">
      <button onClick={togglePlay} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--ink-0)] text-white hover:bg-[var(--ink-1)]">
        {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
      </button>
      <div className="flex-1">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={seek}
          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-gray-200 accent-[var(--ink-0)]"
        />
        <div className="mt-0.5 flex justify-between text-[10px] text-gray-400">
          <span>{formatDurationStr(Math.floor(currentTime))}</span>
          <span>{formatDurationStr(Math.floor(duration))}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Call Row ───

function CallRow({
  call, expanded, onToggle, canDelete, onDelete, deleting, tz, onUpdate,
}: {
  call: CallRecord;
  expanded: boolean;
  onToggle: () => void;
  canDelete: boolean;
  onDelete: () => void;
  deleting: boolean;
  tz?: string;
  onUpdate?: (updated: CallRecord) => void;
}) {
  const contactName = call.contact
    ? `${call.contact.firstName} ${call.contact.lastName}`.trim() || 'Unknown'
    : 'Unknown';
  const agentName = call.agent
    ? `${call.agent.firstName} ${call.agent.lastName}`.trim()
    : '-';
  const isConnected = call.status === 'CONNECTED';

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${isConnected ? 'bg-green-50' : 'bg-red-50'}`}>
          {isConnected ? <Phone size={16} className="text-green-600" /> : <PhoneOff size={16} className="text-red-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 truncate">{contactName}</span>
            {call.disposition && (
              <span
                className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium"
                style={{ backgroundColor: call.disposition.color + '20', color: call.disposition.color }}
              >
                {call.disposition.name}
              </span>
            )}
            {call.recordingUrl && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600">
                <Mic size={10} /> REC
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>{call.phoneNumber}</span>
            <span>{formatDate(call.startTime, tz)}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <div className="text-sm font-semibold text-gray-700">{formatDurationStr(call.duration)}</div>
            <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${statusBadge(call.status)}`}>
              {call.status?.replace(/_/g, ' ') || '-'}
            </span>
          </div>
          {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="bg-gray-50/50 px-4 pb-4 pt-1 space-y-3">
          {/* Meta info */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 text-xs">
            <div className="flex items-center gap-1.5 text-gray-500">
              <User size={12} /> <span>Agent: <strong className="text-gray-700">{agentName}</strong></span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-500">
              <Clock size={12} /> <span>Duration: <strong className="text-gray-700">{formatDurationStr(call.duration)}</strong></span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-500">
              <Calendar size={12} /> <span>{formatDate(call.startTime, tz)}</span>
            </div>
            {call.recordingUrl && (
              <div className="flex items-center gap-1.5 text-[var(--ink-0)]">
                <Phone size={12} /> <span className="font-medium">Recording available</span>
              </div>
            )}
          </div>

          {/* Notes */}
          {call.notes && (
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-500">
                <FileText size={11} /> Notes
              </div>
              <p className="text-sm text-gray-700">{call.notes}</p>
            </div>
          )}

          {/* Audio Player */}
          {call.recordingUrl && (
            <div>
              <div className="mb-1.5 text-xs font-medium text-gray-500">Call Recording</div>
              <AudioPlayer callId={call.id} />
            </div>
          )}

          {/* Call Summary — AI Transcription Analysis */}
          <CallSummarySection
            call={call}
            onUpdate={(updated) => onUpdate?.(updated)}
          />

          {/* Delete button — admin only */}
          {canDelete && call.recordingUrl && (
            <div className="flex justify-end">
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                disabled={deleting}
                className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                Delete Recording
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Call Summary Section (with on-demand Summarize button) ───

function CallSummarySection({ call, onUpdate }: { call: CallRecord; onUpdate: (c: CallRecord) => void }) {
  const [summarizing, setSummarizing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasSummary = call.transcriptionMetadata || call.transcription;
  const hasRecording = !!call.recordingUrl;

  const handleSummarize = async () => {
    setSummarizing(true);
    setError(null);
    try {
      const res = await api.post(`/telecaller/calls/${call.id}/transcribe`);
      const data = res.data?.data;
      if (data) {
        // API returns flat fields; map to CallRecord shape
        const meta: TranscriptionMeta = {
          suggestedDisposition: data.suggestedDisposition,
          suggestedNotes: data.suggestedNotes,
          sentiment: data.sentiment,
          suggestedAction: data.suggestedAction,
          customerIntent: data.customerIntent,
        };
        onUpdate({
          ...call,
          transcription: data.transcription ?? call.transcription,
          transcriptionSummary: data.summary ?? call.transcriptionSummary,
          transcriptionMetadata: meta,
          sentiment: data.sentiment ?? call.sentiment,
        });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to summarize';
      setError(msg.includes('402') || msg.includes('credit') ? 'Insufficient AI credits (needs 8 credits)' : msg);
    } finally {
      setSummarizing(false);
    }
  };

  // Already has summary — show it
  if (hasSummary) {
    return (
      <CallSummaryBlock
        meta={call.transcriptionMetadata}
        summary={call.transcriptionSummary}
        sentiment={call.sentiment}
        transcription={call.transcription}
      />
    );
  }

  // Has recording but no summary — show Summarize button
  if (hasRecording) {
    return (
      <div className="flex items-center gap-3">
        <button
          onClick={handleSummarize}
          disabled={summarizing}
          className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
        >
          {summarizing ? (
            <><Loader2 size={12} className="animate-spin" /> Summarizing...</>
          ) : (
            <><Brain size={12} /> AI Summarize<span className="text-[10px] font-normal text-indigo-400">(8 credits)</span></>
          )}
        </button>
        {error && <span className="text-xs text-red-500">{error}</span>}
      </div>
    );
  }

  // No recording — nothing to show
  return null;
}

// ─── Call Summary (AI Transcription Analysis) ───

function SentimentBadge({ sentiment }: { sentiment: string }) {
  const s = sentiment.toLowerCase();
  const config = s.includes('positive')
    ? { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', label: 'Positive' }
    : s.includes('negative')
    ? { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', label: 'Negative' }
    : { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', label: 'Neutral' };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${config.bg} ${config.text} ${config.border}`}>
      {config.label}
    </span>
  );
}

function CallSummaryBlock({
  meta, summary, sentiment, transcription,
}: {
  meta: TranscriptionMeta | null;
  summary: string | null;
  sentiment: string | null;
  transcription: string | null;
}) {
  const hasMeta = meta && (meta.suggestedDisposition || meta.suggestedNotes || meta.suggestedAction || meta.customerIntent);
  const effectiveSentiment = sentiment || meta?.sentiment;

  return (
    <div className="rounded-lg border border-indigo-200/60 bg-gradient-to-br from-indigo-50/40 to-purple-50/30 p-3 space-y-2.5">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="flex h-5 w-5 items-center justify-center rounded bg-indigo-100">
          <Brain size={12} className="text-indigo-600" />
        </div>
        <span className="text-xs font-semibold text-indigo-700">Call Summary</span>
        {effectiveSentiment && <SentimentBadge sentiment={effectiveSentiment} />}
      </div>

      {/* AI Summary text */}
      {summary && (
        <p className="text-sm leading-relaxed text-gray-700">{summary}</p>
      )}

      {hasMeta && (
        <div className="space-y-2">
          {/* Suggested Disposition */}
          {meta.suggestedDisposition && (
            <div className="flex items-start gap-2 text-xs">
              <Target size={12} className="mt-0.5 shrink-0 text-indigo-400" />
              <div>
                <span className="font-medium text-gray-500">Suggested Disposition:</span>{' '}
                <span className="font-semibold text-gray-800">{meta.suggestedDisposition}</span>
              </div>
            </div>
          )}

          {/* Suggested Action */}
          {meta.suggestedAction && (
            <div className="flex items-start gap-2 text-xs">
              <Sparkles size={12} className="mt-0.5 shrink-0 text-indigo-400" />
              <div>
                <span className="font-medium text-gray-500">Suggested Action:</span>{' '}
                <span className="text-gray-700">{meta.suggestedAction}</span>
              </div>
            </div>
          )}

          {/* AI Notes */}
          {meta.suggestedNotes && (
            <div className="flex items-start gap-2 text-xs">
              <FileText size={12} className="mt-0.5 shrink-0 text-indigo-400" />
              <div>
                <span className="font-medium text-gray-500">AI Notes:</span>{' '}
                <span className="text-gray-700">{meta.suggestedNotes}</span>
              </div>
            </div>
          )}

          {/* Customer Intent Pills */}
          {meta.customerIntent && Object.values(meta.customerIntent).some(Boolean) && (
            <div>
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Customer Intent</div>
              <div className="flex flex-wrap gap-1.5">
                {meta.customerIntent.product && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                    <Tag size={10} /> {meta.customerIntent.product}
                  </span>
                )}
                {meta.customerIntent.location && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                    <MapPin size={10} /> {meta.customerIntent.location}
                  </span>
                )}
                {meta.customerIntent.budget && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                    <IndianRupee size={10} /> {meta.customerIntent.budget}
                  </span>
                )}
                {meta.customerIntent.type && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700">
                    {meta.customerIntent.type}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Full transcription — collapsible toggle */}
      {transcription && <TranscriptionBlock text={transcription} />}
    </div>
  );
}

function TranscriptionBlock({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
      <button onClick={() => setShow(!show)} className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700">
        <FileText size={11} />
        {show ? 'Hide' : 'Show'} Full Transcription
        {show ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {show && <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">{text}</p>}
    </div>
  );
}

// ─── Main Page ───

export function CallLogsPage() {
  const { user } = useAuthStore();
  const roleName = user?.role?.name || '';
  const isAdmin = roleName === 'admin';
  const isSalesHead = roleName === 'manager';
  const canSeeAll = isAdmin || isSalesHead;
  const canDelete = isAdmin;

  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [tenantTz, setTenantTz] = useState<string>('Asia/Kolkata');

  // Load tenant timezone
  useEffect(() => {
    api.get('/settings/timezone').then(res => {
      if (res.data?.data?.value) setTenantTz(res.data.data.value);
    }).catch(() => {});
  }, []);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('all');

  const loadCalls = async (page = 1) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), limit: '20' };

      // Role-based filtering: non-admin/manager only see their own calls
      if (!canSeeAll && user?.id) {
        params.agentId = user.id;
      }

      if (statusFilter) params.status = statusFilter;

      // Date filter
      const now = new Date();
      if (dateFilter === 'today') {
        params.startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        params.endDate = now.toISOString();
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        params.startDate = weekAgo.toISOString();
        params.endDate = now.toISOString();
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        params.startDate = monthAgo.toISOString();
        params.endDate = now.toISOString();
      }

      const res = await api.get('/telecaller/calls', { params });
      let data: CallRecord[] = res.data?.data ?? [];

      // Client-side search filter
      if (search.trim()) {
        const q = search.toLowerCase();
        data = data.filter(c => {
          const name = c.contact ? `${c.contact.firstName} ${c.contact.lastName}`.toLowerCase() : '';
          return name.includes(q) || (c.phoneNumber || '').includes(q);
        });
      }

      setCalls(data);
      setPagination(res.data?.pagination ?? { page, limit: 20, total: 0, totalPages: 0 });
    } catch {
      setCalls([]);
    }
    setLoading(false);
  };

  useEffect(() => { loadCalls(); }, [statusFilter, dateFilter]);

  const handleDelete = async (callId: string) => {
    if (!confirm('Delete this recording? This cannot be undone.')) return;
    setDeletingId(callId);
    try {
      // Delete recording file via the call update — set recordingUrl to null
      await api.delete(`/telecaller/calls/${callId}/recording`);
      setCalls(prev => prev.map(c => c.id === callId ? { ...c, recordingUrl: null } : c));
    } catch {
      alert('Failed to delete recording');
    }
    setDeletingId(null);
  };

  const handleSearch = () => loadCalls(1);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Call Logs</h1>
        <p className="text-sm text-gray-500">
          {canSeeAll ? 'All AURA Sales App calls across the team' : 'Your call history'}
           &middot; Source: AURA Sales App
        </p>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <select
          value={dateFilter}
          onChange={e => setDateFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
        >
          <option value="today">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="all">All Time</option>
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
        >
          <option value="">All Statuses</option>
          <option value="CONNECTED">Connected</option>
          <option value="NO_ANSWER">No Answer</option>
          <option value="BUSY">Busy</option>
          <option value="FAILED">Failed</option>
        </select>
        <button
          onClick={() => loadCalls(1)}
          className="rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
        >
          Search
        </button>
      </div>

      {/* Stats bar */}
      <div className="mb-4 flex items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-xs text-gray-500">
        <span><strong className="text-gray-900">{pagination.total}</strong> calls</span>
        <span><strong className="text-green-700">{calls.filter(c => c.status === 'CONNECTED').length}</strong> connected</span>
        <span><strong className="text-blue-700">{calls.filter(c => c.recordingUrl).length}</strong> with recordings</span>
      </div>

      {/* Call list */}
      <div className="rounded-xl border border-gray-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          </div>
        ) : calls.length === 0 ? (
          <div className="py-16 text-center">
            <Phone size={32} className="mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-400">No calls found</p>
          </div>
        ) : (
          calls.map(call => (
            <CallRow
              key={call.id}
              call={call}
              expanded={expandedId === call.id}
              onToggle={() => setExpandedId(expandedId === call.id ? null : call.id)}
              canDelete={canDelete}
              onDelete={() => handleDelete(call.id)}
              deleting={deletingId === call.id}
              tz={tenantTz}
              onUpdate={(updated) => setCalls(prev => prev.map(c => c.id === updated.id ? updated : c))}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-gray-500">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => loadCalls(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => loadCalls(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
